import React, {useRef, useContext} from "react";
import {useDispatch, useSelector} from "react-redux";
import {updateGuest} from "redux/actions/guestsActions";
import {crmGuestTableColumns, crmV2NewFields} from "configuration/constants.js";
import {fieldParams} from "configuration/specs.js";
import {getEncodedFilters} from "utilities/helperFunctions";
import _ from "lodash";
import CrmColumnsMenu from "../panel/CrmColumnsMenu";
import {getCRMGuestList} from "../../../../redux/actions/crmActions";
import {getCustomFields} from "../../../../redux/actions/settingsActions";
import {useTranslation} from "react-i18next";
import VirtualizedTableComponent from "connectui/component/layout/table/VirtualizedTableComponent";
import Skeleton from "connectui/component/display/Skeleton";
import Container from "connectui/component/layout/Container";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import CheckBox from "connectui/component/input/CheckBox";
import Tooltip from "connectui/component/display/Tooltip";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import {CrmContext} from "../../../../routes/CrmPage";

const useStyles = (theme) =>
  buildStyles({
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.colors.white,
    },
    rowItem: {
      height: "100%",
      display: "flex",
      alignItems: "center",
      minWidth: 0,
      width: "100%",
      flex: "1 1 auto",
      overflow: "hidden",
    },
    tableSection: {
      height: "100%",
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      flexGrow: 1,
      backgroundColor: theme.colors.white,
    },
  });

export default function GuestListTable({
  filters,
  columns,
  updateColumns,
  searchText,
  hideCheckboxColumn,
  setSelectedIds,
  openGuestPanel,
  columnsMenuAnchorEl,
  setColumnsMenuAnchorEl,
}) {
  const {theme} = useTheme();
  const styles = useStyles(theme);
  const dispatch = useDispatch();
  const reducer = useSelector((state) => state.defaultReducer);
  const guests = reducer.guests_dict;
  const [selectedGuests, setSelectedGuests] = React.useState([]);
  const [guestListHits, setGuestListHits] = React.useState([]);
  const [allColumns, setAllColumns] = React.useState([]);
  const [guestListData, setGuestListData] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const currentPageRef = useRef(currentPage);
  const [hasNextPage, setHasNextPage] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const {customFields, setCustomFields} = useContext(CrmContext);

  const {t} = useTranslation();
  const latestRequestRef = React.useRef(0);

  const tableFields = React.useMemo(() => {
    return crmGuestTableColumns;
  }, []);

  const updateCustomFields = () => {
    if (Object.keys(customFields).length) return;
    let isMounted = true;
    dispatch(
      getCustomFields({
        objects: ["guest"],
        onSuccess: (response) => {
          return !isMounted ? null : setCustomFields(response ?? {});
        },
      }),
    );

    return () => {
      isMounted = false;
    };
  };

  React.useEffect(updateCustomFields, []);

  React.useEffect(() => {
    const allAvailableFields = new Set(tableFields);

    if (customFields && Object.keys(customFields).length > 0) {
      const allCustomFields = Object.values(customFields).flat();
      allCustomFields.forEach((field) => {
        if (field.field) {
          allAvailableFields.add(field.field);
        }
      });
    }

    setAllColumns(Array.from(allAvailableFields));
  }, [tableFields, customFields]);

  const rowCount = isLoading
    ? 20
    : searchText && searchText !== ""
      ? guestListHits.length
      : loadingMore
        ? guestListHits.length + 2
        : hasNextPage
          ? guestListHits.length + 1
          : guestListHits.length;

  const toggleSelectAll = (e) => {
    const val = e;
    const newSelectedGuests = val
      ? [...guestListHits.map((g) => g.guest_id), "all"]
      : [];
    setSelectedGuests(newSelectedGuests);
    setSelectedIds(newSelectedGuests.filter((g) => g !== "all"));
  };

  const onRowCheck = (guestId) => {
    let newSelectedGuests;
    if (selectedGuests.includes("all")) {
      newSelectedGuests = selectedGuests.filter(
        (g) => !(g === guestId || g === "all"),
      );
    } else {
      newSelectedGuests = selectedGuests.includes(guestId)
        ? selectedGuests.filter((g) => g !== guestId)
        : [...selectedGuests, guestId];
    }
    setSelectedGuests(newSelectedGuests);
    setSelectedIds(newSelectedGuests.filter((g) => g !== "all"));
  };

  const viewProfile = (index) => {
    const selectedGuest = guestListHits[index];
    if (selectedGuest?.unread_messages) {
      dispatch(
        updateGuest({
          guest_id: selectedGuest.guest_id,
          field: "unread_messages",
          val: false,
          disableAlert: true,
        }),
      );
    }
    openGuestPanel(selectedGuest);
  };

  const isRowLoaded = ({index}) => {
    if (searchText && searchText !== "") return true;
    return index < guestListHits.length;
  };

  const loadNextPage = async () => {
    if (currentPageRef.current === -1 || currentPage === -1) {
      currentPageRef.current = 2;
      setCurrentPage(2);
      return;
    }
    if (loadingMore || !hasNextPage || (searchText && searchText !== ""))
      return;
    setLoadingMore(true);
    const requestId = latestRequestRef.current;
    await dispatch(
      getCRMGuestList(
        null,
        "line_chart",
        null,
        getEncodedFilters(filters, false),
        searchText,
        (newData) => {
          if (requestId !== latestRequestRef.current) return;
          if (newData[0]?.length) {
            setGuestListHits((prev) => [...prev, ...newData[0]]);
            setCurrentPage((prevPage) => {
              const newPage = prevPage + 1;
              currentPageRef.current = newPage;
              return newPage;
            });
            if (newData[0].length < 50) {
              setHasNextPage(false);
            }
          } else {
            setHasNextPage(false);
          }
          setLoadingMore(false);
        },
        () => {
          if (requestId !== latestRequestRef.current) return;
          setLoadingMore(false);
          setHasNextPage(false);
        },
        currentPageRef.current,
        columns,
      ),
    );
  };

  const onClickRow = (index, rowData) => {
    if (!rowData) return;
    if (hideCheckboxColumn) {
      viewProfile(index);
    } else {
      onRowCheck(rowData.guest_id);
    }
  };

  React.useEffect(() => {
    setSelectedGuests([]);
  }, [filters]);

  React.useEffect(() => {
    setGuestListHits((prev) =>
      prev.map((g) => (guests[g.guest_id] ? {...g, ...guests[g.guest_id]} : g)),
    );
  }, [guests]);

  React.useEffect(() => {
    setIsLoading(true);
    const newHits = guestListData?.length ? guestListData[0] : [];
    setGuestListHits(newHits);

    setIsLoading(false);
  }, [guestListData]);

  React.useEffect(() => {
    if (hideCheckboxColumn && selectedGuests.length) setSelectedGuests([]);
  }, [hideCheckboxColumn, selectedGuests]);

  React.useEffect(() => {
    setGuestListHits([]);
    setCurrentPage(-1);
    currentPageRef.current = -1;
    setHasNextPage(true);
    setIsLoading(true);
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    dispatch(
      getCRMGuestList(
        null,
        "line_chart",
        null,
        getEncodedFilters(filters, false),
        searchText,
        (newData) => {
          if (requestId !== latestRequestRef.current) return;
          setGuestListHits(newData[0] || []);
          setGuestListData(newData);
          setIsLoading(false);
          setHasNextPage(newData[0].length >= 50);
        },
        () => {
          if (requestId !== latestRequestRef.current) return;
          setIsLoading(false);
        },
        0,
        columns,
      ),
    );
  }, [filters, searchText, dispatch]);

  const renderCellContent = (value) => {
    if (!value || value === "") {
      return <Container className={styles.rowItem}>-</Container>;
    }

    const displayValue = Array.isArray(value) ? value.join(", ") : value;

    return (
      <Container className={styles.rowItem}>
        <Tooltip
          tooltipContent={displayValue}
          sx={{
            tooltip: {
              maxWidth: "300px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
            trigger: {
              display: "block",
              width: "100%",
            },
          }}
        >
          <Text
            variant="textSM"
            color="textSecondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayValue}
          </Text>
        </Tooltip>
      </Container>
    );
  };

  const tableColumns = React.useMemo(() => {
    const result = [];
    const seenLabels = new Map();
    const customFieldsMap = new Map();

    if (customFields && Object.keys(customFields).length > 0) {
      Object.values(customFields)
        .flat()
        .forEach((field) => {
          if (field.field && field.name) {
            customFieldsMap.set(field.field, field.name);
          }
        });
    }

    if (!hideCheckboxColumn) {
      result.push({
        label: (
          <Container sx={{marginLeft: "4px"}}>
            <CheckBox
              isChecked={selectedGuests.includes("all")}
              onChange={toggleSelectAll}
              outline={true}
            />
          </Container>
        ),
        field: "checkbox",
        sortable: false,
        render: (rowData) => {
          if (!rowData?.guest_id) {
            return <Skeleton width="100%" height={"34px"} />;
          }
          const checked =
            selectedGuests.includes("all") ||
            selectedGuests.includes(rowData?.guest_id);
          return (
            <CheckBox
              isChecked={checked}
              onChange={() => onRowCheck(rowData?.guest_id)}
              outline={true}
            />
          );
        },
      });
    }

    allColumns.forEach((field) => {
      if (!columns.includes(field)) return;

      const label =
        fieldParams[field]?.name || customFieldsMap.get(field) || field;

      if (seenLabels.has(label)) return;
      seenLabels.set(label, true);

      result.push({
        label: label,
        field,
        sortable: false,
        render: (rowData) => {
          if (!rowData?.guest_id) {
            return <Skeleton width="100%" height={"34px"} />;
          }
          return renderCellContent(rowData[field]);
        },
      });
    });

    return result;
  }, [
    hideCheckboxColumn,
    selectedGuests,
    columns,
    toggleSelectAll,
    onRowCheck,
    allColumns,
    customFields,
  ]);

  return (
    <Container className={styles.root}>
      <CrmColumnsMenu
        allColumns={allColumns}
        selectedColumns={columns}
        setSelectedColumns={updateColumns}
        columnsMenuAnchorEl={columnsMenuAnchorEl}
        closeColumnsMenu={() => setColumnsMenuAnchorEl(null)}
        customFields={customFields}
      />

      <Container className={styles.tableSection}>
        {!guestListHits.length && !isLoading ? (
          <>
            <Container height="100%">
              <VirtualizedTableComponent
                columns={tableColumns}
                data={[]}
                gutters={theme.spacing(2)}
                totalRows={0}
                rowHeight={56}
                isRowLoaded={() => true}
                loadMoreRows={() => {}}
              />
            </Container>
            <FlexContainer
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Text variant="displayXS" weight="semibold">
                {t("crm-guest-table-no-results")}
              </Text>
            </FlexContainer>
          </>
        ) : (
          <VirtualizedTableComponent
            columns={tableColumns}
            gutters={theme.spacing(2)}
            data={
              loadingMore
                ? [...guestListHits, ...Array(5).fill({guest_id: null})]
                : guestListHits
            }
            totalRows={rowCount}
            rowHeight={56}
            isRowLoaded={isRowLoaded}
            loadMoreRows={
              !(searchText && searchText !== "") ? loadNextPage : undefined
            }
            onClickRow={onClickRow}
            tableStyles={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              paddingBottom: "8px",
              "& > div": {
                position: "relative",
                zIndex: 1,
              },
            }}
          />
        )}
      </Container>
    </Container>
  );
}
